import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Loader2, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";

const API = process.env.REACT_APP_BACKEND_URL;

export default function PaymentSuccessPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("polling");
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (!sessionId) {
      setStatus("no_session");
      return;
    }
    let cancelled = false;
    let tries = 0;
    const MAX = 30; // ~1 min

    const tick = async () => {
      if (cancelled) return;
      try {
        const r = await axios.get(`${API}/api/payments/status/${sessionId}`);
        if (r.data.payment_status === "paid") {
          setStatus("paid");
          return;
        }
        if (r.data.payment_status === "failed" || r.data.payment_status === "expired") {
          setStatus("failed");
          return;
        }
      } catch (e) { /* transient — keep polling */ }
      tries++;
      setAttempts(tries);
      if (tries >= MAX) { setStatus("timeout"); return; }
      setTimeout(tick, 2000);
    };
    tick();
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-6">
      <div className="max-w-md w-full bg-card border border-border rounded-xl shadow-lg p-8 text-center" data-testid="payment-success-card">
        {status === "polling" && (
          <>
            <Loader2 className="w-12 h-12 text-navy animate-spin mx-auto" />
            <h1 className="font-display text-2xl font-bold mt-4">Confirming payment…</h1>
            <p className="text-sm text-muted-foreground mt-2">
              Stripe is finalising your subscription. This usually takes a few seconds.
            </p>
            <p className="text-xs text-muted-foreground mt-4">Attempt {attempts + 1} / 30</p>
          </>
        )}
        {status === "paid" && (
          <>
            <div className="w-16 h-16 rounded-full bg-emerald-500/10 flex items-center justify-center mx-auto">
              <CheckCircle2 className="w-10 h-10 text-emerald-600" />
            </div>
            <h1 className="font-display text-3xl font-bold mt-4">You're in.</h1>
            <div className="gold-divider mx-auto my-4 max-w-[80px]" />
            <p className="text-sm text-muted-foreground">
              Welcome aboard. Your dealership tenant is provisioned and ready.
              Sign in with the email + password you just set to see your dashboard.
            </p>
            <Button onClick={() => navigate("/login")} className="w-full mt-6 group" data-testid="payment-success-signin-btn">
              Sign in <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-0.5 transition-transform" />
            </Button>
          </>
        )}
        {status === "failed" && (
          <>
            <h1 className="font-display text-2xl font-bold">Payment failed</h1>
            <p className="text-sm text-muted-foreground mt-2">
              Stripe declined the charge. No account was activated. Try again or use a different card.
            </p>
            <Link to="/signup" className="inline-block mt-4 text-navy underline">Back to signup</Link>
          </>
        )}
        {status === "timeout" && (
          <>
            <h1 className="font-display text-2xl font-bold">Still processing</h1>
            <p className="text-sm text-muted-foreground mt-2">
              Stripe is taking longer than usual. Your subscription may still activate — try signing in in a minute.
              If nothing happens, contact support.
            </p>
            <Button onClick={() => navigate("/login")} className="w-full mt-4">Try signing in</Button>
          </>
        )}
        {status === "no_session" && (
          <>
            <h1 className="font-display text-2xl font-bold">No session found</h1>
            <p className="text-sm text-muted-foreground mt-2">This page is meant to be reached from Stripe checkout.</p>
            <Link to="/signup" className="inline-block mt-4 text-navy underline">Start signup</Link>
          </>
        )}
      </div>
    </div>
  );
}
